import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, CheckCircle2 } from "lucide-react";

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-md bg-slate-900 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-amber-500" />
          </div>
          <span className="font-display font-bold text-slate-900">High-Ticket Blueprint</span>
        </Link>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7">
          <h1 className="font-display text-2xl font-bold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  const emailSent = !!result?.email_sent;

  return (
    <Shell title="Reset your password" subtitle="Enter your email and we'll create a reset link.">
      {!result ? (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" />
          </div>
          <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 h-11" disabled={loading} data-testid="forgot-submit-button">
            {loading ? "Sending…" : "Create reset link"}
          </Button>
        </form>
      ) : (
        <div className="space-y-4" data-testid="forgot-result">
          <div className="flex items-start gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            <span>{result.message}</span>
          </div>
          {emailSent && (
            <p className="text-sm text-slate-500">We've emailed a reset link to <span className="font-medium text-slate-700">{email}</span>. It's valid for one hour — check your inbox (and spam folder).</p>
          )}
          {result.google_only ? (
            <Link to="/login"><Button className="w-full bg-slate-900 hover:bg-slate-800">Back to login</Button></Link>
          ) : (
            <Link to="/login" className="text-sm text-slate-900 underline inline-block">Back to login</Link>
          )}
        </div>
      )}
      <p className="text-sm text-slate-500 text-center mt-6">
        Remembered it? <Link to="/login" className="text-slate-900 font-medium underline">Log in</Link>
      </p>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <Shell title="Choose a new password" subtitle="Enter a new password for your account.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="reset-error">{error}</p>}
        {!token && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">Missing reset token. Request a new link from the forgot-password page.</p>}
        <div className="space-y-1.5">
          <Label htmlFor="password">New password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirm password</Label>
          <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-input" />
        </div>
        <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 h-11" disabled={loading || !token} data-testid="reset-submit-button">
          {loading ? "Saving…" : "Set new password"}
        </Button>
      </form>
    </Shell>
  );
}
